import React, { useEffect, useState } from 'react';
import { Endpoint } from '../../../models/api.model';
import { useSnackbar } from '../../../hooks/snackbar';
import { useParams } from 'react-router-dom';
import { useBrandedNavigate } from '../../../hooks/useBrandedNavigate';
import { Spinner } from '../../../components/shared/Spinner';
import {
  useIframePageGetBySlugQuery,
  IframePageGetBySlugQuery,
} from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/iframe-page-get-by-slug.query.generated';
import './IframePage.scss';
import { useUserContext } from '../../../providers/UserProvider';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import { useAuth } from 'react-oidc-context';
import bingoWheelImage from '../../../assets/Wheels/bingo_wheel_star.png';
import spinsWheelImage from '../../../assets/Wheels/spins_wheel_star.png';
import bingoWheelLoggedOut from '../../../assets/Wheels/bingo_wheel.png';
import spinsWheelLoggedOut from '../../../assets/Wheels/spins_wheel.png';
import { Paragraph } from '../../../components/shared/Paragraph';
import { Button } from '../../../components/shared/Button';
import { useSignIn } from '../../../hooks/auth';
import { BRANDS } from '../../../components/Itv/types';
import domPurify from 'dompurify';

type IframePageData = IframePageGetBySlugQuery['iframe_pages'][number];
type WheelBrand = null | 'bingo' | 'spins';

export const IframePage: React.FC = () => {
  const { t } = useTranslations();
  const { navigate, navigateHome, registrationUrl } = useBrandedNavigate();
  const { slug = '', brand = '' } = useParams();
  const { addSnack } = useSnackbar();
  const [iframeData, setIframeData] = useState<IframePageData | null>(null);
  const [wheelBrand, setWheelBrand] = useState<WheelBrand>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const { userProfileId, theme, setTheme } = useUserContext();
  const auth = useAuth();
  const signIn = useSignIn();

  const handleError = (): void => {
    addSnack({
      type: 'error',
      theme: 'dark',
      message: t('errors.general'),
    });
    navigateHome();
  };

  const { loading } = useIframePageGetBySlugQuery({
    variables: {
      slug,
    },
    onCompleted: (data) => {
      if (!data.iframe_pages.length) {
        return handleError();
      }

      setIframeData(data.iframe_pages[0]);
    },
    onError: (error) => {
      return handleError();
    },
    context: { endpoint: Endpoint.betzoneDirectusAPI },
  });

  const getIframeSrc = (): string => {
    if (!iframeData) return '';

    const baseSrc = wheelBrand === 'spins' ? iframeData.src_spins : iframeData.src;

    if (!baseSrc) return '';

    const url = new URL(baseSrc);

    if (userProfileId) {
      url.searchParams.append('token', userProfileId);
    }

    const finalUrl = url.toString();

    console.log('Iframe URL:', finalUrl, '| Brand:', wheelBrand);

    return finalUrl;
  };

  const onClickLogin = (): void => {
    signIn();
  };

  const onClickRegister = (): void => {
    navigate(registrationUrl);
  };

  const onSelectBrand = (brand: 'bingo' | 'spins'): void => {
    setIframeLoaded(false);
    setWheelBrand(brand);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (brand) setTheme(brand as BRANDS);
  }, []);

  const onBackClick = (): void => {
    setWheelBrand(null);
  };
  const termsBrandedData = brand === BRANDS.BINGO ? iframeData?.bingo_terms : iframeData?.spins_terms;

  const defaultFallbackTermsData = termsBrandedData || iframeData?.terms;
  const formattedTerms = defaultFallbackTermsData
    ? domPurify
        .sanitize(defaultFallbackTermsData, { ADD_ATTR: ['target', 'rel'] })
        .replace(/<a([^>]*)>/g, '<a$1 class="iframe-page__terms-link-highlight">')
    : null;

  if (wheelBrand === null) {
    return (
      <div className="iframe-page__brand-selection">
        <div className="iframe-page__brand-selection-content">
          <h1 className="iframe-page__brand-selection-title">{t('wheel-selection.title')}</h1>
          <div className="iframe-page__brand-selection-buttons">
            <div className="iframe-page__brand-selection-button-container">
              <img src={bingoWheelImage} alt="Bingo Wheel" className="iframe-page__brand-selection-wheel-image" />
              <Button
                onClick={(): void => onSelectBrand('bingo')}
                variant="primary"
                size="large"
                type="button"
                className="iframe-page__brand-selection-btn--bingo"
              >
                {t('wheel-selection.bingo.button')}
              </Button>
            </div>
            <div className="iframe-page__brand-selection-button-container">
              <img src={spinsWheelImage} alt="Spins Wheel" className="iframe-page__brand-selection-wheel-image" />
              <Button
                onClick={(): void => onSelectBrand('spins')}
                variant="primary"
                size="large"
                type="button"
                className="iframe-page__brand-selection-btn--spins"
              >
                {t('wheel-selection.spins.button')}
              </Button>
            </div>
          </div>
          {formattedTerms && (
            <div
              className={`iframe-page__terms iframe-page__terms--${brand || 'bingo'}`}
              dangerouslySetInnerHTML={{
                __html: formattedTerms,
              }}
            ></div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {loading ? (
        <div className="spinner-wrapper">
          <Spinner />
        </div>
      ) : !iframeData ? (
        <div>No iframe data found</div>
      ) : auth.isAuthenticated ? (
        <div className="iframe-page">
          <div className="iframe-page__header">
            <button onClick={onBackClick} type="button" className="iframe-page__back-btn">
              <span className="iframe-page__back-icon" />
              {t('wheel-selection.back')}
            </button>
          </div>
          <div className="iframe-page__iframe-container">
            {!iframeLoaded && <p className="iframe-page__loading-text">{t('wheel-selection.loading')}</p>}
            <iframe
              src={getIframeSrc()}
              title={iframeData.title || 'Game'}
              onLoad={(): void => setIframeLoaded(true)}
            />
          </div>
          {formattedTerms && (
            <div
              className={`iframe-page__terms iframe-page__terms--${brand || 'bingo'}`}
              dangerouslySetInnerHTML={{ __html: formattedTerms }}
            ></div>
          )}
        </div>
      ) : (
        <div className="error-page">
          <div className="error-page__content">
            <div className="error-page__image-section">
              <img
                src={theme === BRANDS.SPINS ? spinsWheelLoggedOut : bingoWheelLoggedOut}
                alt="Wheel"
                className="iframe-error-page__image"
              />
            </div>
            <div className="error-page__text-section">
              <Paragraph size="md" className="error-page__message">
                Please log in or sign up today to play {iframeData.title}.
              </Paragraph>
              <div>
                <Button
                  onClick={onClickLogin}
                  variant="secondary-alt"
                  size="large"
                  className="iframe-page__cta-btn"
                  type="button"
                >
                  LOGIN
                </Button>
                <Button
                  onClick={onClickRegister}
                  variant="primary"
                  size="large"
                  className="iframe-page__cta-btn"
                  type="button"
                >
                  REGISTER
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
